import React, {useEffect, useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import {ArrowLeft} from 'lucide-react'
import MuxPlayer from '@mux/mux-player-react'
import {sanity, urlFor} from '../lib/sanity'

const placeholderImage = '/assets/images/placeholderEventImage.jpg'

const SPEECH_QUERY = `*[
  _type == "speech" &&
  slug.current == $slug
][0]{
  _id,
  title,
  "slug": slug.current,
  speaker,
  date,
  location,
  occasion,
  image,
  body,
  "playbackId": coalesce(
    videoFile.asset->playbackId,
    videoFile.asset->data.playback_ids[0].id
  ),
  "videoStatus": videoFile.asset->status
}`

function formatDate(dateString) {
  if (!dateString) return ''

  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(dateString))
}

function getImageSrc(image) {
  const hasSanityImage = image?.asset?._ref || image?.asset?._id

  return hasSanityImage
    ? urlFor(image).width(1200).height(1200).url()
    : placeholderImage
}

function SpeechPage() {
  const {slug} = useParams()
  const [speech, setSpeech] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    sanity
      .fetch(SPEECH_QUERY, {slug})
      .then((result) => {
        setSpeech(result)
        setLoading(false)
      })
      .catch((error) => {
        console.error(error)
        setLoading(false)
      })
  }, [slug])

  if (loading) return null

  if (!speech) {
    return (
      <main className="min-h-screen px-8 py-24 text-[#5757fd]">
        <p>Toespraak niet gevonden.</p>
      </main>
    )
  }

  const speechBody = speech.body?.split(/\n\s*\n/) || []

  return (
    <main className="min-h-screen px-2 pt-24 text-[#5757fd] md:px-8 md:pb-12">
      <section className="mx-auto max-w-4xl">
        <div className="mb-8">
          <Link
            to="/toespraken"
            className="inline-flex items-center rounded-full bg-white px-5 py-3 text-sm font-sans_bold text-[#5757fd] transition-colors hover:bg-[#f3f2ff] hover:text-[#5757fd]/80"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Alle toespraken
          </Link>
        </div>

        <article className="overflow-hidden rounded-3xl bg-white px-6 py-6 shadow-sm md:px-8 md:py-8">
          <div className="aspect-square overflow-hidden rounded-sm bg-[#f3f2ff]">
            <img
              src={getImageSrc(speech.image)}
              alt={speech.image?.alt || speech.speaker || speech.title}
              className="h-full w-full object-cover"
            />
          </div>

          <div className="mx-auto max-w-3xl pt-10 text-center">
            <div className="mb-8 flex flex-wrap justify-center gap-2">
              {speech.date && (
                <span className="rounded-full bg-[#f3f2ff] px-4 py-2 text-sm font-sans_bold">
                  {formatDate(speech.date)}
                </span>
              )}

              {speech.location && (
                <span className="rounded-full bg-[#f3f2ff] px-4 py-2 text-sm font-sans_bold">
                  {speech.location}
                </span>
              )}

              {speech.occasion && (
                <span className="rounded-full bg-[#f3f2ff] px-4 py-2 text-sm font-sans_bold">
                  {speech.occasion}
                </span>
              )}
            </div>

            <h1 className="text-4xl font-sans_bold leading-[0.95] tracking-tight md:text-5xl lg:text-6xl">
              {speech.title}
            </h1>

            {speech.speaker && (
              <p className="mt-6 text-lg font-sans_bold">
                Spreker: {speech.speaker}
              </p>
            )}
          </div>

          <section className="mx-auto mt-8 max-w-2xl border-t border-[#5757fd]/30 pt-14 md:mt-20 md:pb-16 md:pt-16">
  <div className="space-y-6 text-lg font-sans">
    {speechBody.map((paragraph, index) => (
      <p key={index} className="whitespace-pre-line">
        {paragraph}
      </p>
    ))}
  </div>
</section>

{speech.playbackId && (
  <section className="mx-auto mt-4 max-w-3xl border-t border-[#5757fd]/30 pt-10 md:mt-0 md:pt-14">
    <div className="aspect-video overflow-hidden rounded-2xl bg-[#5757fd]">
      <MuxPlayer
        playbackId={speech.playbackId}
        streamType="on-demand"
        accentColor="#5757fd"
        metadata={{
          video_title: speech.title,
        }}
        className="h-full w-full"
        style={{
          width: '100%',
          height: '100%',
          '--media-object-fit': 'cover',
          '--media-object-position': 'center',
        }}
      />
    </div>
  </section>
)}
        </article>
      </section>
    </main>
  )
}

export default SpeechPage